import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, imageUrl, formatNZD, timeAgo } from "@/lib/api";
import { MapPin, Phone, Store as StoreIcon, Navigation, Share2, Clock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function DealPage() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/markdowns/${id}`);
        setM(data);
        setState("ok");
      } catch (e) {
        setState("missing");
      }
    })();
  }, [id]);

  if (state === "loading") return <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500">Loading…</div>;
  if (state === "missing" || !m) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <h1 className="font-display text-3xl font-bold">Deal not found or expired</h1>
      <p className="text-slate-600 mt-2">Check the home feed for fresh markdowns.</p>
      <Link to="/"><Button className="mt-6 bg-emerald-800 hover:bg-emerald-900">Back to deals</Button></Link>
    </div>
  );

  const url = window.location.href;
  const off = m.percent_off || 0;
  const directions = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(m.store?.address || m.store?.store_name || "")}`;

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: `${m.product_name} — ${off}% OFF`, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    } catch (e) { /* cancelled */ }
  };

  const savings = Math.max((m.was_price || 0) - (m.now_price || 0), 0);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-emerald-800 font-semibold mb-4"><ArrowLeft className="w-4 h-4" />Back to all deals</Link>
      <div className="grid md:grid-cols-2 gap-8 bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
        <div className="relative aspect-[4/3] md:aspect-auto bg-slate-100">
          <img src={imageUrl(m.image_path)} alt={m.product_name} className="w-full h-full object-cover" />
          {off > 0 && <div className="absolute top-4 left-4 discount-pulse bg-red-600 text-white text-sm font-black uppercase tracking-wider px-3 py-1.5 rounded-full">{off}% OFF</div>}
        </div>
        <div className="p-6 sm:p-8 space-y-4">
          <div className="text-xs font-black uppercase tracking-widest text-emerald-800 bg-emerald-50 inline-block px-2.5 py-1 rounded-full">In-store only · {m.store?.region}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight" data-testid="deal-title">{m.product_name}</h1>
          {m.description && <p className="text-slate-600">{m.description}</p>}
          <div className="flex items-baseline gap-3">
            <span className="font-display text-4xl font-black text-emerald-800">{formatNZD(m.now_price)}</span>
            <span className="strike text-lg">{formatNZD(m.was_price)}</span>
          </div>
          <div className="text-sm text-amber-700 font-semibold">You save {formatNZD(savings)}</div>
          {m.expires_at && <div className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Deal ends {new Date(m.expires_at).toLocaleString("en-NZ")}</div>}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Available at</div>
            <Link to={`/store/${m.store?.slug}`} className="font-display font-bold text-lg text-slate-900 hover:text-emerald-800">{m.store?.store_name}</Link>
            {m.store?.address && <div className="text-sm text-slate-600 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{m.store.address}</div>}
            {m.store?.phone && <div className="text-sm text-slate-600 flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{m.store.phone}</div>}
          </div>
          <div className="flex gap-2 pt-2">
            <a href={directions} target="_blank" rel="noreferrer" className="flex-1"><Button className="w-full bg-emerald-800 hover:bg-emerald-900 h-11" data-testid="deal-directions"><Navigation className="w-4 h-4 mr-1.5" />Get directions</Button></a>
            <Button variant="outline" onClick={share} className="h-11" data-testid="deal-share"><Share2 className="w-4 h-4 mr-1.5" />Share</Button>
          </div>
          <div className="border-t border-slate-100 pt-4 flex gap-4 items-center" data-testid="deal-qr">
            <img alt="Scan to redeem in-store" src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(url)}`} className="w-32 h-32 rounded-lg bg-white border border-slate-200 p-1" />
            <div className="text-xs text-slate-600">
              <div className="font-display font-bold text-slate-900 mb-1">Show this in-store</div>
              Staff scans the QR to confirm the deal — proof of foot-traffic Blurblive brought in.
            </div>
          </div>
          <div className="text-[11px] text-slate-400">Posted {timeAgo(m.created_at)}</div>
        </div>
      </div>
    </div>
  );
}
